import {useState, useMemo} from 'react';
import _ from 'lodash';

import {runCode} from '../../api/api';
import {getDraftKey, getMostRecentDraft} from '../../util';

const LAST_USED_LANGUAGE = 'last_used_language';

const emptyCode = {
  slug: null,
  title: '',
  testCases: {},
  pythonCode: '',
  pythonTests: '',
  javascriptCode: '',
  javascriptTests: '',
  csharpCode: '',
  csharpTests: '',
  javaCode: '',
  javaTests: '',
  cppCode: '',
  cppTests: '',
  cCode: '',
  cTests: '',
  goCode: '',
  goTests: '',
  rubyCode: '',
  rubyTests: '',
  swiftCode: '',
  swiftTests: '',
};

export const Languages = [{
  value: 'python',
  label: 'Python',
  code: 'pythonCode',
  tests: 'pythonTests',
}, {
  value: 'javascript',
  label: 'JavaScript',
  code: 'javascriptCode',
  tests: 'javascriptTests',
}, {
  value: 'csharp',
  label: 'C#',
  code: 'csharpCode',
  tests: 'csharpTests',
}, {
  value: 'java',
  label: 'Java',
  code: 'javaCode',
  tests: 'javaTests',
}, {
  value: 'cpp',
  label: 'C++',
  code: 'cppCode',
  tests: 'cppTests',
}, {
  value: 'c',
  label: 'C',
  code: 'cCode',
  tests: 'cTests',
}, {
  value: 'go',
  label: 'Go',
  code: 'goCode',
  tests: 'goTests',
}, {
  value: 'ruby',
  label: 'Ruby',
  code: 'rubyCode',
  tests: 'rubyTests',
}, {
  value: 'swift',
  label: 'Swift',
  code: 'swiftCode',
  tests: 'swiftTests',
}];

const formatTestResults = (results, code) => {
  /*
  * Some languages terminate their testing as soon as a test fails
  * Thus total number of test cases are not properly reflected.
  * If CodeProblem.testCases exists, retrieve "total" from there
  */
  let {total} = results;
  if (code.testCases) total = code.testCases.length;

  /*
  * Only render individual test cases results if CodeProblem.testCases
  * and individual test case results exist. If not, simply render how
  * many tests passed or failed
  */
  let testCases;
  if (code.testCases && results.testCaseResults) {
    testCases = code.testCases.map((meta, index) => {
      let testCaseResult = {passed: false, actual: undefined, ran: false};

      // Some languages have tests that terminate testing as soon as the first fails
      if (index < results?.testCaseResults?.length) {
        testCaseResult = {
          ...results.testCaseResults[index],
          ran: true,
        };
      }

      return {
        ...meta,
        ...testCaseResult,
      };
    });
  }

  return {...results, total, testCases};
};

export default function CodeEngine({
  code = emptyCode,
  draftSlug = code.slug,
  initialLanguage,
  children,
  onEditCode,
  onRunCode,
  onRunTests,
  onPassTests,
  onChangeLanguage: onChangeLanguageProp,
  disableDrafts = false,
  enableAllLanguages = false,
}) {
  const languageOptions = useMemo(() => (enableAllLanguages
    ? Languages
    : Languages.filter((lang) => code[lang.code] || code[lang.tests])
  ), [code]);

  const initializeLanguage = () => {
    if (initialLanguage) return initialLanguage;

    const lastUsedLanguage = localStorage.getItem(LAST_USED_LANGUAGE);

    if (disableDrafts) {
      return languageOptions.find(({value}) => value === lastUsedLanguage)?.value
      || languageOptions[0]?.value;
    }

    const mostRecentDraft = getMostRecentDraft(draftSlug);
    return mostRecentDraft?.language
      || languageOptions.find(({value}) => value === lastUsedLanguage)?.value
      || languageOptions[0]?.value;
  };

  const initializeContent = (language) => () => {
    if (disableDrafts) return code[language?.code];

    const mostRecentDraft = getMostRecentDraft(draftSlug);
    return mostRecentDraft?.data || code[language?.code];
  };

  const [activeLanguage, setActiveLanguage] = useState(initializeLanguage);
  const language = Languages.find(({value}) => value === activeLanguage);
  const [content, setContent] = useState(initializeContent(language));
  const [isRunning, setRunning] = useState(false);
  const [consoleResults, setConsoleResults] = useState(null);
  const [isTesting, setTesting] = useState(false);
  const [hasPassedTests, setHasPassedTests] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const testCode = code[language?.tests];

  const onChange = (value) => {
    onEditCode?.({language: activeLanguage});
    setContent(value);

    if (!disableDrafts) {
      const draftKey = getDraftKey(draftSlug);
      const localStorageDrafts = JSON.parse(localStorage.getItem(draftKey));
      localStorage.setItem(draftKey,
        JSON.stringify({
          ...localStorageDrafts,
          [activeLanguage]: {
            time: Date.now(),
            data: value,
          },
        }));
    }
  };

  const onReset = () => onChange(code[language?.code]);

  const onChangeLanguage = (languageKey) => {
    onChangeLanguageProp?.(languageKey);
    setActiveLanguage(languageKey);
    localStorage.setItem(LAST_USED_LANGUAGE, languageKey);

    // Update content with either the relevant draft or the default code
    const newLanguage = languageOptions.find((lang) => lang.value === languageKey);

    if (disableDrafts) {
      setContent(code[newLanguage?.code] || '');
    } else {
      const draftKey = getDraftKey(draftSlug);
      const localStorageDrafts = JSON.parse(localStorage.getItem(draftKey));
      setContent(localStorageDrafts?.[languageKey]?.data || code[newLanguage?.code] || '');
    }
  };

  const run = async () => {
    onRunCode?.({language: activeLanguage});
    setRunning(true);
    const res = await runCode({
      body: content,
      language: language.value,
    });
    const execResp = await res.json();
    const results = execResp.data;

    setTestResults(null);
    setConsoleResults(results);
    setRunning(false);

    return results;
  };

  const test = async () => {
    onRunTests?.({language: activeLanguage});
    setTesting(true);
    const res = await runCode({
      body: content,
      language: language.value,
      fixture: testCode,
    });

    const execResp = await res.json();
    const formattedResults = formatTestResults(execResp.data, code);

    setConsoleResults(null);
    setTestResults(formattedResults);
    setTesting(false);

    if (!hasPassedTests && formattedResults.failed === 0 && formattedResults.passed > 0) {
      setHasPassedTests(true);
      onPassTests?.({language: activeLanguage});
    }

    return formattedResults;
  };

  const onFakeSubmit = () => setRunning(true);

  const onFakeSubmitFinished = (results) => {
    setTestResults(null);
    setConsoleResults(results);
    setRunning(false);
  };

  const onFakeTest = () => setTesting(true);

  const onFakeTestFinished = (results) => {
    const formattedResults = formatTestResults(results, code);
    setTestResults(formattedResults);
    setTesting(false);
    setHasPassedTests((curr) => !curr && results.failed === 0 && results.passed > 0);
  };

  return children({
    codeProblem: code,
    content,
    languageOptions,
    activeLanguage,
    isRunning,
    isTesting,
    hasTests: !!testCode,
    testCode,
    testResults,
    consoleResults,
    dirty: content !== (code[language?.code] || ''),
    onChange,
    onSubmit: run,
    onTest: test,
    onChangeLanguage,
    onReset,
    onFakeSubmit,
    onFakeSubmitFinished,
    onFakeTest,
    onFakeTestFinished,
  });
}
